import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { usePageTitle } from '../hooks/usePageTitle';
import {
  ArrowLeftIcon,
  BuildingOffice2Icon,
  UserCircleIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

const formatDateTime = (iso: string | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  const s = String(d.getSeconds()).padStart(2, '0');
  return `${y}-${m}-${day} ${h}:${min}:${s}`;
};

const AdministrationSubscriptionDetail: React.FC = () => {
  const { t } = useTranslation('subscriptions');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  usePageTitle(t('detailPageTitle'));

  const { data: subData, isLoading: subLoading, error: subError } = useQuery({
    queryKey: ['subscription', id],
    queryFn: () => apiService.getSubscriptionById(id!),
    enabled: !!id && user?.role === 'sysadmin',
  });

  const [licenseStatusFilter, setLicenseStatusFilter] = useState<string>('all');
  const [licenseTypeFilter, setLicenseTypeFilter] = useState<string>('all');

  const { data: licensesData, isLoading: licensesLoading } = useQuery({
    queryKey: ['subscription-licenses', id, licenseStatusFilter, licenseTypeFilter],
    queryFn: () => apiService.getLicensesBySubscriptionId(id!, { status: licenseStatusFilter, type: licenseTypeFilter }),
    enabled: !!id && user?.role === 'sysadmin',
  });

  const subscription = subData?.subscription as Record<string, unknown> | undefined;
  const entityName = subData?.entity_name as string | undefined;
  const licenses = licensesData?.licenses ?? [];
  const isLoading = subLoading || licensesLoading;

  if (!user) return null;
  if (user.role !== 'sysadmin') {
    return (
      <div className="p-6">
        <p className="text-gray-600 dark:text-gray-400">{t('noPermission')}</p>
      </div>
    );
  }

  if (subError || (!subLoading && !subscription)) {
    return (
      <div className="p-6">
        <p className="text-gray-600 dark:text-gray-400">{t('subscriptionNotFound')}</p>
        <button
          type="button"
          onClick={() => navigate('/administration/subscriptions')}
          className="mt-2 text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1"
        >
          <ArrowLeftIcon className="w-4 h-4" /> {t('backToSubscriptions')}
        </button>
      </div>
    );
  }

  const entityType = (subscription?.entity_type as string) ?? '';
  const entityId = (subscription?.entity_id as string) ?? '';
  const isCompany = entityType === 'company';
  const entityHref = isCompany ? `/clients/${entityId}` : `/users/${entityId}`;

  return (
    <div className="p-4 sm:p-6 max-w-4xl">
      <button
        type="button"
        onClick={() => navigate('/administration/subscriptions')}
        className="mb-4 flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
      >
        <ArrowLeftIcon className="w-4 h-4" /> {t('backToSubscriptions')}
      </button>

      {isLoading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white" />
        </div>
      )}

      {!isLoading && subscription && (
        <>
          <div className="mb-6 flex items-center gap-3">
            {isCompany ? (
              <BuildingOffice2Icon className="w-10 h-10 text-indigo-500" />
            ) : (
              <UserCircleIcon className="w-10 h-10 text-gray-500" />
            )}
            <div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                {isCompany ? t('companySubscription') : t('userSubscription')}
              </h1>
              <a href={entityHref} className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">
                {entityName || entityId}
              </a>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-sm font-medium text-gray-900 dark:text-white">{t('subscription')}</h2>
            </div>
            <dl className="px-4 py-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-gray-500 dark:text-gray-400">{t('status')}</dt>
                <dd className="font-medium text-gray-900 dark:text-white">{String(subscription.status ?? '—')}</dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">{t('plan')}</dt>
                <dd className="font-medium text-gray-900 dark:text-white">{String(subscription.plan_name ?? '—')}</dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">{t('maxFullLicenses')}</dt>
                <dd className="font-medium text-gray-900 dark:text-white">{subscription.max_full_licenses != null ? String(subscription.max_full_licenses) : '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">{t('licensedUntil')}</dt>
                <dd className="font-mono text-gray-900 dark:text-white">
                  {subscription.licensed_until ? formatDateTime(subscription.licensed_until as string) : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">{t('trialEndsAt')}</dt>
                <dd className="font-mono text-gray-900 dark:text-white">
                  {subscription.trial_ends_at ? formatDateTime(subscription.trial_ends_at as string) : '—'}
                </dd>
              </div>
            </dl>
          </div>

          <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-gray-500" />
                <h2 className="text-sm font-medium text-gray-900 dark:text-white">{t('licensesCount', { count: licenses.length })}</h2>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={licenseStatusFilter}
                  onChange={(e) => setLicenseStatusFilter(e.target.value)}
                  className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-2 py-1.5 text-sm"
                >
                  <option value="all">{t('all')}</option>
                  <option value="active">Active</option>
                  <option value="revoked">Revoked</option>
                </select>
                <select
                  value={licenseTypeFilter}
                  onChange={(e) => setLicenseTypeFilter(e.target.value)}
                  className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-2 py-1.5 text-sm"
                >
                  <option value="all">{t('all')}</option>
                  <option value="full">Full</option>
                  <option value="reader">Reader</option>
                </select>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800/50">
                  <tr>
                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('user')}</th>
                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('type')}</th>
                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('status')}</th>
                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('created')}</th>
                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('revokedAt')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {licenses.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                        {t('noActiveLicenses')}
                      </td>
                    </tr>
                  ) : (
                    licenses.map((lic: { id: string; user_id: string; user_email?: string; type: string; status: string; created_at: string; revoked_at?: string }) => (
                      <tr key={lic.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-4 py-2 text-sm">
                          <Link to={`/users/${lic.user_id}`} className="text-indigo-600 dark:text-indigo-400 hover:underline">
                            {lic.user_email ?? lic.user_id}
                          </Link>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">{lic.type}</td>
                        <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">{lic.status}</td>
                        <td className="px-4 py-2 text-sm font-mono text-gray-600 dark:text-gray-300">{formatDateTime(lic.created_at)}</td>
                        <td className="px-4 py-2 text-sm font-mono text-gray-600 dark:text-gray-300">{lic.revoked_at ? formatDateTime(lic.revoked_at) : '—'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdministrationSubscriptionDetail;
